import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { surveyAPI } from '../../services/api';
import { SURVEY_ID, SURVEY_TITLE, SECTIONS, countAnswered } from './surveyQuestions';
import ResponseSummary from './ResponseSummary';

// ---------------------------------------------------------------------------
// Profile card for the clinician survey: where the user stands (not started /
// in progress / submitted) and, once submitted, their answers — the background
// section up front, the rest on demand. It reads the latest *submitted* copy,
// so the profile updates whenever the survey is (re)submitted.
// ---------------------------------------------------------------------------

const ALL_PAGES = SECTIONS.flatMap((s) => s.pages);
const formatDate = (d) => (d ? new Date(d).toLocaleDateString([], { dateStyle: 'medium' }) : '');

const BADGE = {
  none: 'bg-gray-100 text-gray-600',
  progress: 'bg-amber-100 text-amber-800',
  submitted: 'bg-emerald-100 text-emerald-800',
};

const ProfileSurveyCard = () => {
  const [{ loading, error, response }, setState] = useState({ loading: true, error: false, response: null });
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    surveyAPI.getMyResponse(SURVEY_ID)
      .then((res) => { if (!cancelled) setState({ loading: false, error: false, response: res.data?.response || null }); })
      .catch(() => { if (!cancelled) setState({ loading: false, error: true, response: null }); });
    return () => { cancelled = true; };
  }, []);

  // Submitted copy: `submitted_answers` from the API; failing that (e.g. an
  // older backend that doesn't send it), a completed response's own answers.
  const submittedAnswers = response?.submitted_answers
    || (response?.status === 'completed' ? response.answers || {} : null);
  const pendingEdits = !!submittedAnswers && response.status !== 'completed';

  let badge = null;
  let detail = '';
  let action = 'Take the survey';
  if (submittedAnswers) {
    badge = ['submitted', 'Submitted'];
    detail = `Submitted ${formatDate(response.completed_at)}`
      + (response.submission_count > 1 ? ` · last updated ${formatDate(response.last_submitted_at)}` : '');
    action = pendingEdits ? 'Continue editing' : 'View or edit';
  } else if (response) {
    const [answered, total] = countAnswered(ALL_PAGES, response.answers || {});
    badge = ['progress', 'In progress'];
    detail = `${answered} of ${total} questions answered so far.`;
    action = 'Continue survey';
  } else if (!loading && !error) {
    badge = ['none', 'Not started'];
    detail = 'You haven’t taken the survey yet. Your answers will appear here once you submit it.';
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className="bg-white rounded-2xl shadow-lg p-6"
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 flex items-center flex-wrap gap-2">
            <ClipboardDocumentListIcon className="h-6 w-6 text-primary-600" />
            {SURVEY_TITLE}
            {badge && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${BADGE[badge[0]]}`}>
                {badge[1]}
              </span>
            )}
          </h3>
          {loading && <p className="text-sm text-gray-400 mt-1">Loading your survey responses…</p>}
          {error && <p className="text-sm text-rose-600 mt-1">Couldn’t load your survey responses. Please refresh to try again.</p>}
          {detail && <p className="text-sm text-gray-500 mt-1">{detail}</p>}
        </div>
        {!loading && !error && (
          <Link
            to="/survey"
            className="self-start inline-flex items-center px-4 py-2 border border-primary-600 text-primary-600 rounded-lg text-sm font-medium hover:bg-primary-50 transition-colors whitespace-nowrap"
          >
            {action}
          </Link>
        )}
      </div>

      {submittedAnswers && (
        <>
          {pendingEdits && (
            <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              You have unsubmitted changes on the Survey page. This shows your last submitted answers.
            </p>
          )}
          <ResponseSummary answers={submittedAnswers} sectionIds={showAll ? undefined : [SECTIONS[0].id]} />
          <button
            onClick={() => setShowAll((v) => !v)}
            className="mt-4 text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            {showAll ? 'Show background only' : `Show all answers (${SECTIONS.length} sections)`}
          </button>
        </>
      )}
    </motion.div>
  );
};

export default ProfileSurveyCard;
